import { useState, useEffect, useCallback } from 'react';
import { Download, Loader2, History, Save, FileDown, RefreshCw, Edit3 } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import type { SupplierStockItem } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import ChangeHistoryView from './ChangeHistoryView';
import StockCorrectionDialog from './StockCorrectionDialog';
import StockInputHistoryDrawer from './StockInputHistoryDrawer';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { RowsPerPageSelect } from '../../ui/rows-per-page';
import { toast } from 'sonner';
import { useFormatters } from '../../../contexts/PreferencesContext';
import FileDropZone from '../../common/FileDropZone';

export default function StockUploadTab() {
  const { formatDate } = useFormatters();
  const [stockItems, setStockItems] = useState<SupplierStockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [activeSubTab, setActiveSubTab] = useState('stock');
  // Hide rows whose part_number or description starts with the literal
  // "part" (case-insensitive). Filter is applied client-side over the
  // currently loaded page only — pagination + totalCount remain backend-driven.
  const [hideTestParts, setHideTestParts] = useState(false);
  // Track pending edits: { [itemId]: quantityString }
  const [pendingEdits, setPendingEdits] = useState<Record<number, string>>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [correctTarget, setCorrectTarget] = useState<SupplierStockItem | null>(null);
  const [historyTarget, setHistoryTarget] = useState<SupplierStockItem | null>(null);
  const [pageSize, setPageSize] = useState(50);

  const pendingCount = Object.keys(pendingEdits).length;

  const fetchStock = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page,
        page_size: pageSize,
      };
      if (search) params['search'] = search;

      const res = await callOffAPI.getSupplierStock(params);
      setStockItems(res.data.items || []);
      setTotalCount(res.data.total_count || 0);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load stock';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    if (activeSubTab === 'stock') fetchStock();
  }, [activeSubTab, fetchStock]);

  // Refetch the stock list when the window regains focus, so Running Total
  // picks up call-offs that were dispatched in another tab. Skip when there
  // are pending edits — refetching would wipe the user's in-progress work.
  useEffect(() => {
    const handleFocus = () => {
      if (activeSubTab !== 'stock') return;
      if (Object.keys(pendingEdits).length > 0) return;
      fetchStock();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [activeSubTab, pendingEdits, fetchStock]);

  // Customer review 11 Sep (drag and drop everywhere): fed by FileDropZone.
  const handleFileUpload = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await callOffAPI.uploadStock(formData);
      toast.success(
        `Upload complete: ${res.data.created} created, ${res.data.updated} updated`
      );
      if (res.data.errors.length > 0) {
        toast.error(`${res.data.errors.length} errors during upload`);
      }
      setPendingEdits({});
      fetchStock();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleQtyChange = (itemId: number, value: string) => {
    // The Stock Input cell is an additive entry — backend increments
    // warehouse_stock by this value on save. Empty or 0 means "nothing to
    // add right now", so drop the pending edit instead of queuing a no-op.
    const trimmed = value.trim();
    if (trimmed === '' || parseFloat(trimmed) === 0) {
      setPendingEdits(prev => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    } else {
      setPendingEdits(prev => ({ ...prev, [itemId]: value }));
    }
  };

  const handleSaveAll = async () => {
    const entries = Object.entries(pendingEdits);
    if (entries.length === 0) return;

    // Each pending value is an additive entry — backend adds it to
    // warehouse_stock. Skip 0/empty so we don't post no-op rows that just
    // pollute the change log.
    const items: Array<{ item_code: string; quantity: number }> = [];
    for (const [idStr, value] of entries) {
      const id = Number(idStr);
      const item = stockItems.find(s => s.id === id);
      if (!item) continue;
      const trimmed = value.trim();
      if (trimmed === '') continue;
      const qty = parseFloat(trimmed);
      if (isNaN(qty) || qty < 0) {
        toast.error(`Invalid quantity for ${item.part_number}`);
        return;
      }
      if (qty === 0) continue;
      items.push({ item_code: item.part_number, quantity: qty });
    }

    if (items.length === 0) {
      toast.info('Nothing to save — all pending values are zero or empty.');
      setPendingEdits({});
      setEditingId(null);
      return;
    }

    setSaving(true);
    try {
      const res = await callOffAPI.bulkSaveStock({ items });
      const { saved, errors } = res.data;

      if (errors.length > 0) {
        // Surface the actual rejection reasons (PO-cap, etc.) — caps at 3
        // lines to keep the toast readable when many rows fail at once.
        const preview = errors.slice(0, 3).join('\n');
        const more = errors.length > 3 ? `\n…and ${errors.length - 3} more` : '';
        toast.error(`${errors.length} item${errors.length !== 1 ? 's' : ''} failed to save`, {
          description: preview + more,
          duration: 10000,
        });
      }
      if (saved > 0) {
        toast.success(`${saved} item${saved !== 1 ? 's' : ''} saved`);
      }
      setPendingEdits({});
      setEditingId(null);
      fetchStock();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await callOffAPI.exportStock('xlsx');
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'supplier_stock_export.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Stock exported');
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportHistory = async (format: 'xlsx' | 'csv') => {
    setExporting(true);
    try {
      const res = await callOffAPI.exportChangeHistory(format);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock_change_history.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Change history exported');
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await callOffAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'stock_upload_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Download failed';
      toast.error(message);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="outline" className="gap-2" onClick={handleDownloadTemplate}>
          <Download className="h-4 w-4" />
          Download Template
        </Button>
        <FileDropZone
          size="compact"
          accept=".xlsx,.xls"
          busy={uploading}
          hint="Upload stock — Excel (.xlsx, .xls)"
          onFiles={(files) => void handleFileUpload(files)}
          className="min-w-[22rem]"
          data-testid="stock-upload-tab-drop-zone"
        />
        {activeSubTab === 'history' ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" disabled={exporting}>
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                Export History
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExportHistory('xlsx')}>
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportHistory('csv')}>
                CSV (.csv)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button variant="outline" className="gap-2" onClick={handleExport} disabled={exporting}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            Export
          </Button>
        )}
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList>
          <TabsTrigger value="stock">Current Stock</TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            Change History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="stock" className="mt-4">
          <div className="flex items-center justify-between mb-4 gap-4">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search by part number or description..."
                className="w-80"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
              <select
                value={hideTestParts ? 'hide_part' : 'all'}
                onChange={(e) => setHideTestParts(e.target.value === 'hide_part')}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label="Row filter"
              >
                <option value="all">Show all parts</option>
                <option value="hide_part">Hide &ldquo;part&rdquo;-prefixed</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <RowsPerPageSelect
                value={pageSize}
                onChange={(n) => { setPageSize(n); setPage(1); }}
                disabled={pendingCount > 0}
                disabledTitle="Save or discard pending edits first"
              />
              <Button
                variant="outline"
                className="gap-2"
                onClick={fetchStock}
                disabled={loading || pendingCount > 0}
                title={pendingCount > 0 ? 'Save or discard pending edits first' : 'Reload Running Total after a dispatch'}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Refresh
              </Button>
              {pendingCount > 0 && (
                <Button className="gap-2" onClick={handleSaveAll} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Changes ({pendingCount})
                </Button>
              )}
            </div>
          </div>
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : stockItems.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No stock data available.
                </div>
              ) : (() => {
                const visibleItems = hideTestParts
                  ? stockItems.filter(
                      (it) =>
                        !(it.part_number || '').toLowerCase().startsWith('part') &&
                        !(it.description || '').toLowerCase().startsWith('part'),
                    )
                  : stockItems;
                if (visibleItems.length === 0) {
                  return (
                    <div className="text-center py-12 text-muted-foreground text-sm">
                      All {stockItems.length} rows on this page were hidden by the active filter.
                    </div>
                  );
                }
                return (
                <Table wrapperClassName="max-h-[calc(100vh-340px)]">
                  <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-background [&_th]:shadow-[inset_0_-1px_0_0_hsl(var(--border))]">
                    <TableRow>
                      <TableHead>Part Number</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Stock Input</TableHead>
                      <TableHead className="text-right">Warehouse Stock</TableHead>
                      <TableHead>UoM</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead>Updated By</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleItems.map((item) => {
                      const hasPending = item.id in pendingEdits;
                      return (
                        <TableRow key={item.id} className={hasPending ? 'bg-yellow-50' : ''}>
                          <TableCell className="font-medium font-mono text-xs">
                            <button
                              type="button"
                              onClick={() => setHistoryTarget(item)}
                              className="text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
                              title="View input history for this part"
                            >
                              {item.part_number}
                            </button>
                          </TableCell>
                          <TableCell className="text-xs max-w-[250px] truncate">{item.description}</TableCell>
                          <TableCell className="text-right">
                            {editingId === item.id || hasPending ? (
                              <Input
                                type="number"
                                min={0}
                                step="0.001"
                                placeholder="0"
                                className="h-7 w-28 text-right text-sm ml-auto"
                                autoFocus={editingId === item.id}
                                value={hasPending ? pendingEdits[item.id] : ''}
                                onChange={(e) => handleQtyChange(item.id, e.target.value)}
                                onBlur={() => { if (!hasPending) setEditingId(null); }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') { setEditingId(null); }
                                }}
                              />
                            ) : (
                              <span
                                className="inline-block h-7 w-28 leading-7 text-right text-sm cursor-pointer rounded px-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                                onClick={() => setEditingId(item.id)}
                                title="Click to add stock for this part"
                              >
                                0
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium">{item.running_stock_total}</TableCell>
                          <TableCell>{item.unit_of_measure}</TableCell>
                          <TableCell>{item.last_updated ? formatDate(item.last_updated) : ''}</TableCell>
                          <TableCell>{item.updated_by_name}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCorrectTarget(item)}
                              title="Correct the running warehouse total (requires reason)"
                              disabled={hasPending}
                            >
                              <Edit3 className="h-4 w-4 mr-1" />
                              <span className="text-xs">Correct</span>
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                );
              })()}
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <ChangeHistoryView pageSize={pageSize} />
        </TabsContent>
      </Tabs>

      {correctTarget && (
        <StockCorrectionDialog
          open={!!correctTarget}
          onOpenChange={(open) => { if (!open) setCorrectTarget(null); }}
          partNumber={correctTarget.part_number}
          description={correctTarget.description}
          currentQty={correctTarget.running_stock_total}
          unitOfMeasure={correctTarget.unit_of_measure}
          onCorrected={() => { setCorrectTarget(null); fetchStock(); }}
        />
      )}

      {historyTarget && (
        <StockInputHistoryDrawer
          open={!!historyTarget}
          onOpenChange={(open) => { if (!open) setHistoryTarget(null); }}
          partNumber={historyTarget.part_number}
          description={historyTarget.description}
        />
      )}
    </div>
  );
}
